import { Combobox } from "@base-ui/react/combobox";
import { Select } from "@base-ui/react/select";
import { useDebouncedEffect } from "@react-hookz/web";
import { useRef, useState } from "react";
import {
  fetchSwiftsellMaterials,
  fetchSwiftsellSkus,
  SwiftsellMaterialOption,
  SwiftsellSkuOption,
  validateSwiftsellProducts,
} from "../../api/swiftsell";
import { getEffectiveAccountId } from "../../redux/selectors/ui.selector";
import { useAppSelector } from "../../utils/hooks";
import s from "./InStoreSkuOrder.module.scss";
import { buildProducts } from "./cartPayloadBuilder";
import { parseProductsCsv } from "./csvParser";
import { findLegOption, getLegOptionsForSku, LegOption, NO_LEG_OPTION } from "./legOptions";
import { ProductTypeIcon } from "./ProductTypeIcon";
import { getProductType } from "./productType";
import { LineItem, lineItemKey, mergeLineItems } from "./lineItemMerger";
import { skuHasLeg } from "./skuCategory";

const formatMaterial = (material: SwiftsellMaterialOption) =>
  `${material.sku} - ${material.description}${material.grade ? ` (${material.grade})` : ""}`;

const ChevronDown = ({ className }: { className?: string }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

export default function InStoreSkuOrder() {
  const effectiveAccountId = useAppSelector(getEffectiveAccountId);
  const skuRequestIdRef = useRef(0);
  const materialRequestIdRef = useRef(0);
  const [skuInput, setSkuInput] = useState("");
  const [skuOptions, setSkuOptions] = useState<SwiftsellSkuOption[]>([]);
  const [selectedSku, setSelectedSku] = useState<SwiftsellSkuOption | null>(null);
  const [selectedLeg, setSelectedLeg] = useState<LegOption | null>(null);
  const [isSkuLoading, setIsSkuLoading] = useState(false);

  const [materialInput, setMaterialInput] = useState("");
  const [materialOptions, setMaterialOptions] = useState<SwiftsellMaterialOption[]>([]);
  const [selectedMaterial, setSelectedMaterial] = useState<SwiftsellMaterialOption | null>(null);
  const [isMaterialLoading, setIsMaterialLoading] = useState(false);

  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Leg options are restricted by the SKU's prefix (DL / DB1 / DB2). Only show the picker when
  // there is a real choice to make; a single option (DB2 -> No leg) is applied automatically.
  const legChoices = selectedSku ? getLegOptionsForSku(selectedSku.sku) : [];
  const showLegPicker = legChoices.length > 1;

  useDebouncedEffect(
    () => {
      const search = skuInput.trim();
      const requestId = skuRequestIdRef.current + 1;
      skuRequestIdRef.current = requestId;

      if (search.length < 2) {
        setSkuOptions([]);
        setIsSkuLoading(false);
        return;
      }

      setIsSkuLoading(true);
      fetchSwiftsellSkus(search, effectiveAccountId)
        .then((options) => {
          if (requestId === skuRequestIdRef.current) setSkuOptions(options);
        })
        .catch(() => {
          if (requestId === skuRequestIdRef.current) setSkuOptions([]);
        })
        .finally(() => {
          if (requestId === skuRequestIdRef.current) setIsSkuLoading(false);
        });
    },
    [effectiveAccountId, skuInput],
    250
  );

  useDebouncedEffect(
    () => {
      // Fabrics are scoped to the selected SKU + account: no SKU picked -> nothing to show.
      if (!selectedSku) {
        setMaterialOptions([]);
        return;
      }

      const search =
        selectedMaterial && materialInput === formatMaterial(selectedMaterial)
          ? selectedMaterial.sku
          : materialInput.trim();
      const requestId = materialRequestIdRef.current + 1;
      materialRequestIdRef.current = requestId;

      setIsMaterialLoading(true);
      fetchSwiftsellMaterials(search, selectedSku.sku, effectiveAccountId)
        .then((options) => {
          if (requestId === materialRequestIdRef.current) setMaterialOptions(options);
        })
        .catch(() => {
          if (requestId === materialRequestIdRef.current) setMaterialOptions([]);
        })
        .finally(() => {
          if (requestId === materialRequestIdRef.current) setIsMaterialLoading(false);
        });
    },
    [materialInput, selectedMaterial, selectedSku, effectiveAccountId],
    250
  );

  const resetConfig = () => {
    setSelectedSku(null);
    setSelectedLeg(null);
    setSkuInput("");
    setSkuOptions([]);
    setSelectedMaterial(null);
    setMaterialInput("");
    setMaterialOptions([]);
  };

  const addLineItem = () => {
    setError("");
    setMessage("");

    if (!selectedSku) {
      setError("Select a SKU first.");
      return;
    }

    if (!selectedMaterial) {
      setError("Select a fabric first.");
      return;
    }

    let leg: LegOption | null = null;
    if (legChoices.length === 1) {
      // Single option (DB2 -> No leg): apply it without asking.
      leg = legChoices[0];
    } else if (legChoices.length > 1) {
      if (!selectedLeg) {
        setError("Select a leg first.");
        return;
      }
      leg = selectedLeg;
    }

    setLineItems((current) =>
      mergeLineItems([...current, { sku: selectedSku.sku, fabric: selectedMaterial, quantity: 1, leg }])
    );
    resetConfig();
    setMessage("Item added.");
  };

  const removeLineItem = (target: LineItem) => {
    const key = lineItemKey(target);
    setLineItems((current) => current.filter((item) => lineItemKey(item) !== key));
    setMessage("");
  };

  const handleCsvUpload = async (file: File) => {
    setError("");
    setMessage("");

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setError("Upload a .csv file.");
      return;
    }

    try {
      const text = await file.text();
      const rows = parseProductsCsv(text);

      // Each row carries its own fabric; rows without one fall back to the selected fabric.
      const rowsWithFabric = rows.map((row) => ({
        ...row,
        fabricSku: row.fabric || selectedMaterial?.sku || "",
      }));

      if (rowsWithFabric.some((row) => !row.fabricSku)) {
        setError('CSV must contain a "fabric" column or select a fabric first.');
        return;
      }

      setIsSending(true);
      const validation = await validateSwiftsellProducts(
        rowsWithFabric.map((row) => ({ sku: row.sku, fabric: row.fabricSku })),
        effectiveAccountId
      );

      if (validation.invalidSkus.length > 0) {
        setError(`Invalid SKU: ${validation.invalidSkus.join(", ")}`);
        return;
      }

      if (validation.invalidFabrics.length > 0) {
        setError(`Invalid fabric: ${validation.invalidFabrics.join(", ")}`);
        return;
      }

      if (validation.incompatiblePairs.length > 0) {
        const pairs = validation.incompatiblePairs.map((pair) => `${pair.sku} + ${pair.fabric}`).join(", ");
        setError(`Fabric not available for this SKU/account: ${pairs}`);
        return;
      }

      const fabricBySku = new Map<string, SwiftsellMaterialOption>();
      validation.results.forEach((row) => {
        if (row.resolvedFabric) fabricBySku.set(row.resolvedFabric.sku, row.resolvedFabric);
      });

      const csvItems: LineItem[] = [];
      const unknownLegs: string[] = [];

      for (const row of rowsWithFabric) {
        const fabric = fabricBySku.get(row.fabricSku);
        if (!fabric) {
          setError("Fabric was not found.");
          return;
        }

        let leg: LegOption | null = null;
        if (skuHasLeg(row.sku)) {
          if (row.leg) {
            const option = findLegOption(row.leg);
            if (option) leg = option;
            else unknownLegs.push(row.leg);
          } else {
            // Blank leg on a leg-bearing item = No leg, so we never send a SKU missing its leg digit.
            leg = NO_LEG_OPTION;
          }
        }

        csvItems.push({ sku: row.sku, fabric, quantity: row.quantity, leg });
      }

      if (unknownLegs.length > 0) {
        setError(`Unknown leg: ${Array.from(new Set(unknownLegs)).join(", ")}`);
        return;
      }

      setLineItems((current) => mergeLineItems([...current, ...csvItems]));
      setMessage("CSV loaded.");
    } catch (csvError) {
      setError(csvError instanceof Error ? csvError.message : "CSV could not be loaded.");
    } finally {
      setIsSending(false);
    }
  };

  const sendToSwiftsell = () => {
    setError("");
    setMessage("");

    if (lineItems.length === 0) {
      setError("Add at least one item.");
      return;
    }

    setIsSending(true);

    const products = buildProducts(lineItems);

    const cartInfo = {
      products,
      shortCode: window.location.href,
      renderImgUrl: "no-image",
      isActive: true,
    };

    console.log("Posting cart info to parent window:", cartInfo);

    window.parent.postMessage(
      {
        event_id: "returnCartInfo",
        cartInfo,
      },
      "*"
    );

    setIsSending(false);
    setMessage("Sent to cart.");
  };

  return (
    <div className={s.wrapper}>
      <div className={s.title}>In-store order</div>
      <div className={s.controls}>
        <div className={s.field}>
          <label className={s.label} htmlFor="in-store-sku-combobox">
            SKU
          </label>
          <Combobox.Root
            items={skuOptions}
            value={selectedSku}
            inputValue={skuInput}
            itemToStringLabel={(item) => item.sku}
            itemToStringValue={(item) => item.sku}
            isItemEqualToValue={(item, value) => item.sku === value.sku}
            onInputValueChange={(value) => {
              skuRequestIdRef.current += 1;
              setSkuInput(value);

              if (selectedSku && value !== selectedSku.sku) {
                setSelectedSku(null);
                setSelectedLeg(null);
                // Fabric depends on the SKU, so clearing the SKU clears the fabric too.
                setSelectedMaterial(null);
                setMaterialInput("");
                setMaterialOptions([]);
              }
            }}
            onValueChange={(value) => {
              if (value) {
                setSelectedSku(value);
                setSelectedLeg(null);
                setSkuInput(value.sku);
                // New SKU -> reset the fabric so the list reloads for this SKU's allowed grades.
                setSelectedMaterial(null);
                setMaterialInput("");
                setMaterialOptions([]);
                setMessage("");
                setError("");
              }
            }}
            autoHighlight
          >
            <Combobox.InputGroup className={s.inputGroup}>
              <Combobox.Input id="in-store-sku-combobox" className={s.input} placeholder="Enter SKU" />
              <Combobox.Trigger className={s.trigger} aria-label="Open SKU list">
                <ChevronDown className={s.chevron} />
              </Combobox.Trigger>
            </Combobox.InputGroup>
            <Combobox.Portal>
              <Combobox.Positioner className={s.popupPositioner} sideOffset={4}>
                <Combobox.Popup className={s.popup}>
                  {isSkuLoading ? (
                    <div className={s.empty}>Loading...</div>
                  ) : (
                    <>
                      <Combobox.Empty>
                        <div className={s.empty}>
                          {skuInput.trim().length < 2 ? "Enter at least 2 characters." : "No SKUs found."}
                        </div>
                      </Combobox.Empty>
                      <Combobox.List className={s.list}>
                        {(item: SwiftsellSkuOption) => (
                          <Combobox.Item key={item.sku} value={item} className={s.item}>
                            <span className={s.itemRow}>
                              <ProductTypeIcon type={getProductType(item.sku)} className={s.typeIcon} />
                              <span className={s.itemSku}>{item.sku}</span>
                            </span>
                          </Combobox.Item>
                        )}
                      </Combobox.List>
                    </>
                  )}
                </Combobox.Popup>
              </Combobox.Positioner>
            </Combobox.Portal>
          </Combobox.Root>
        </div>

        <div className={s.field}>
          <label className={s.label} htmlFor="in-store-material-combobox">
            Fabric
          </label>
          <Combobox.Root
            items={materialOptions}
            value={selectedMaterial}
            inputValue={materialInput}
            disabled={!selectedSku}
            itemToStringLabel={formatMaterial}
            itemToStringValue={(item) => item.sku}
            isItemEqualToValue={(item, value) => item.sku === value.sku}
            onInputValueChange={(value) => {
              materialRequestIdRef.current += 1;
              setMaterialInput(value);

              if (selectedMaterial && value !== formatMaterial(selectedMaterial)) {
                setSelectedMaterial(null);
              }
            }}
            onValueChange={(value) => {
              if (value) {
                setSelectedMaterial(value);
                setMaterialInput(formatMaterial(value));
                setMessage("");
                setError("");
              }
            }}
            autoHighlight
            openOnInputClick
          >
            <Combobox.InputGroup className={s.inputGroup}>
              <Combobox.Input
                id="in-store-material-combobox"
                className={s.input}
                placeholder={selectedSku ? "Enter Fabric" : "Select a SKU first"}
              />
              <Combobox.Trigger className={s.trigger} aria-label="Open fabric list">
                <ChevronDown className={s.chevron} />
              </Combobox.Trigger>
            </Combobox.InputGroup>
            <Combobox.Portal>
              <Combobox.Positioner className={s.popupPositioner} sideOffset={4}>
                <Combobox.Popup className={s.popup}>
                  {isMaterialLoading ? (
                    <div className={s.empty}>Loading...</div>
                  ) : (
                    <>
                      <Combobox.Empty>
                        <div className={s.empty}>No fabrics found.</div>
                      </Combobox.Empty>
                      <Combobox.List className={s.list}>
                        {(item: SwiftsellMaterialOption) => (
                          <Combobox.Item key={item.sku} value={item} className={s.item}>
                            <span className={s.itemSku}>{item.sku}</span>
                            <span className={s.itemMeta}>
                              {item.description}
                              {item.grade ? ` / ${item.grade}` : ""}
                            </span>
                          </Combobox.Item>
                        )}
                      </Combobox.List>
                    </>
                  )}
                </Combobox.Popup>
              </Combobox.Positioner>
            </Combobox.Portal>
          </Combobox.Root>
        </div>

        {showLegPicker && (
          <div className={s.field}>
            <label className={s.label} htmlFor="in-store-leg-select">
              Leg
            </label>
            <Select.Root
              value={selectedLeg?.code ?? null}
              onValueChange={(value) => {
                setSelectedLeg(legChoices.find((option) => option.code === value) ?? null);
                setMessage("");
                setError("");
              }}
            >
              <Select.Trigger id="in-store-leg-select" className={s.selectTrigger}>
                <Select.Value className={s.selectValue}>
                  {(value: string | null) => {
                    const option = legChoices.find((item) => item.code === value);
                    return option ? option.name : <span className={s.selectPlaceholder}>Select a leg</span>;
                  }}
                </Select.Value>
                <Select.Icon className={s.trigger} aria-label="Open leg list">
                  <ChevronDown className={s.chevron} />
                </Select.Icon>
              </Select.Trigger>
              <Select.Portal>
                <Select.Positioner
                  className={s.popupPositioner}
                  sideOffset={4}
                  side="bottom"
                  align="start"
                  alignItemWithTrigger={false}
                >
                  <Select.Popup className={s.popup}>
                    <Select.List className={s.list}>
                      {legChoices.map((option) => (
                        <Select.Item key={option.code} value={option.code} className={s.item}>
                          <Select.ItemText>{option.name}</Select.ItemText>
                        </Select.Item>
                      ))}
                    </Select.List>
                  </Select.Popup>
                </Select.Positioner>
              </Select.Portal>
            </Select.Root>
          </div>
        )}

        <button className={s.submit} type="button" onClick={addLineItem} disabled={isSending}>
          Add
        </button>
      </div>

      <div className={s.uploadRow}>
        <label className={s.uploadButton} htmlFor="in-store-csv-upload">
          Upload CSV
        </label>
        <input
          id="in-store-csv-upload"
          className={s.fileInput}
          type="file"
          accept=".csv,text/csv"
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            event.currentTarget.value = "";

            if (file) {
              handleCsvUpload(file);
            }
          }}
        />
        <span className={s.uploadHint}>Columns: sku, fabric, quantity, leg</span>
      </div>

      {lineItems.length > 0 && (
        <div className={s.selectedList}>
          {lineItems.map((item) => (
            <div className={s.chip} key={lineItemKey(item)}>
              <span>{item.sku}</span>
              <span className={s.itemMeta}>
                {item.fabric.sku}
                {item.leg ? ` · ${item.leg.name}` : ""}
              </span>
              <span className={s.chipCount}>x{item.quantity}</span>
              <button
                className={s.chipRemove}
                type="button"
                aria-label={`Remove ${item.sku}`}
                onClick={() => removeLineItem(item)}
              >
                <XIcon />
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        className={s.submit}
        type="button"
        onClick={sendToSwiftsell}
        disabled={isSending || lineItems.length === 0}
      >
        Add to Cart
      </button>

      <div className={`${s.message} ${error ? s.error : ""}`}>{error || message}</div>
    </div>
  );
}
